import React, { useState } from "react";
import { Tag, Check, AlertCircle, X } from "lucide-react";

const formatBRL = (n) =>
  (typeof n === "number" ? n : Number(n || 0)).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  });

/**
 * Componente de input para cupom de desconto
 * Gerencia a entrada, validação e exibição do status do cupom
 */
export default function CouponInput({
  onValidate,
  onClear,
  loading = false,
  error = null,
  couponData = null,
  totalValue = 0,
  discountValue = 0,
}) {
  const [code, setCode] = useState("");
  const [isApplied, setIsApplied] = useState(false);

  const handleValidate = async () => {
    if (!code.trim()) return;

    const result = await onValidate(code.trim().toUpperCase());
    if (result && result.valid) {
      setIsApplied(true);
    }
  };

  const handleClear = () => {
    setCode("");
    setIsApplied(false);
    onClear();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isApplied) {
      handleValidate();
    }
  };

  return (
    <div className="space-y-3">
      {/* Input e botão */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyPress={handleKeyPress}
            placeholder="Código do cupom"
            disabled={isApplied || loading}
            className={`w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm transition-colors ${
              isApplied
                ? "bg-green-50 border-green-300 text-green-800"
                : error
                ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20"
            } focus:outline-none focus:ring-2 disabled:bg-gray-100 disabled:cursor-not-allowed`}
          />
        </div>

        {!isApplied ? (
          <button
            type="button"
            onClick={handleValidate}
            disabled={loading || !code.trim()}
            className="w-full sm:w-auto px-6 py-2.5 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Validando...
              </>
            ) : (
              "Aplicar"
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleClear}
            className="w-full sm:w-auto px-6 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
          >
            <X className="w-4 h-4" />
            Remover
          </button>
        )}
      </div>

      {/* Feedback de sucesso */}
      {isApplied && couponData && (
        <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              Cupom aplicado com sucesso!
            </p>
            <p className="text-xs text-green-700 mt-1">
              {couponData.discount_type === "percentage"
                ? `Desconto de ${couponData.discount_value}%`
                : `Desconto de ${formatBRL(couponData.discount_value)}`}
              {" • "}
              Economia: {formatBRL(discountValue)}
            </p>
            {couponData.description && (
              <p className="text-xs text-green-600 mt-1">
                {couponData.description}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Feedback de erro */}
      {error && !isApplied && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Cupom inválido</p>
          </div>
        </div>
      )}
    </div>
  );
}
